import { CalendarClock, Check, Loader2, RefreshCw, X } from 'lucide-react';

import { formatUsd } from '@/lib/format';
import { useRentalSubscribe } from '@/hooks/useRentalSubscribe';

/**
 * RecurringRentalModal — turn autopay on/off for a rental kind.
 *
 * Model (see docs/HANDOFF-RENTAL-AUTOPAY.md): autopay is driven by OUR billing
 * systems, NOT QuickBooks recurring-template autopay. The switch lives here in
 * the order app; the charge runs in the billing system that owns the rental
 * (equipment → APBG-Leasing-Rental; tank → a separate system). This modal
 * records the customer's choice in orders.rental_subscriptions; the executor
 * reads that enrollment and charges the saved method when it bills the rental.
 *
 * Phase 1 (pending QBO Payments card-on-file): a saved-method capture step gets
 * added here and the executor wiring goes live. Until then this records intent
 * and the "On auto pay" state reflects that choice.
 */

const KIND_LABEL: Record<'tank' | 'equipment', string> = {
  tank: 'gas cylinder rental',
  equipment: 'equipment rental',
};

interface Props {
  customerId: string;
  rentalKind: 'tank' | 'equipment';
  sourceInvoiceId: string;
  amount: number;
  alreadyOn: boolean; // currently enrolled → show "manage / turn off"
  onClose: () => void;
}

export function RecurringRentalModal({ customerId, rentalKind, sourceInvoiceId, amount, alreadyOn, onClose }: Props) {
  const subscribe = useRentalSubscribe();
  const kindLabel = KIND_LABEL[rentalKind];

  function enroll() {
    subscribe.mutate(
      { customer_id: customerId, rental_kind: rentalKind, action: 'enroll', source_qbo_invoice_id: sourceInvoiceId, amount },
      { onSuccess: () => onClose() },
    );
  }
  function cancel() {
    subscribe.mutate(
      { customer_id: customerId, rental_kind: rentalKind, action: 'cancel' },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 md:p-4">
      <div className="w-full md:max-w-md bg-brix-surface rounded-t-2xl md:rounded-2xl shadow-card-deep border border-brix-border overflow-hidden">
        <div className="bg-brix-primary px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <CalendarClock size={16} />
            <span className="font-semibold">{alreadyOn ? 'Manage automatic payment' : 'Set up automatic payment'}</span>
          </div>
          <button type="button" onClick={onClose} disabled={subscribe.isPending} className="text-white/80 hover:text-white disabled:opacity-40" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-700 leading-relaxed">
            {alreadyOn ? (
              <>Your <strong>{kindLabel}</strong> is set to pay automatically each month — new rental
              invoices are charged for you and won't sit in your open-invoice list.</>
            ) : (
              <>Turn on autopay for your <strong>{kindLabel}</strong> (about <strong>{formatUsd(amount)}/mo</strong>).
              Each new rental invoice is then paid automatically — you won't have to pay it by hand.</>
            )}
          </p>

          <p className="text-[11px] text-brix-muted leading-relaxed bg-brix-surface-alt/60 border border-brix-border rounded-lg px-3 py-2">
            Applies to your {kindLabel} invoices only — product orders are never auto-charged. We're
            rolling out automatic payment now; we'll confirm your saved payment method once it's active.
          </p>

          {subscribe.isError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{subscribe.error.message}</p>
          )}

          {alreadyOn ? (
            <button
              type="button"
              onClick={cancel}
              disabled={subscribe.isPending}
              className="w-full px-5 py-3 rounded-xl border border-red-300 text-red-700 text-sm font-semibold disabled:opacity-50 hover:bg-red-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              {subscribe.isPending ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={15} />}
              Turn off automatic payment
            </button>
          ) : (
            <button
              type="button"
              onClick={enroll}
              disabled={subscribe.isPending}
              className="w-full px-5 py-3 rounded-xl bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50 hover:bg-emerald-700 transition-colors inline-flex items-center justify-center gap-2"
            >
              {subscribe.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={15} />}
              Turn on automatic payment
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
